import { useEffect, useRef, useState } from 'react'
import ssapi from 'lib/api/secondstepApi'
import { transformNetSuiteCustomerForForm } from 'lib/netsuiteUtils'
import { SchoolLookUpSiteData } from 'types'

interface UseNetSuiteLookupOptions {
  nsCustomerId?: string
  onLookupComplete: (schoolData: SchoolLookUpSiteData) => void
  enabled: boolean
}

/**
 * Hook for looking up NetSuite customer by nsCustomerId on mount
 * Used for initial data loading when modal opens with existing site data
 */
export const useNetSuiteLookup = ({
  nsCustomerId,
  onLookupComplete,
  enabled,
}: UseNetSuiteLookupOptions) => {
  const [isLoading, setIsLoading] = useState(false)
  const hasLookedUpCustomer = useRef(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    hasLookedUpCustomer.current = false
  }, [nsCustomerId])

  useEffect(() => {
    isMountedRef.current = true

    const lookupNetSuiteCustomer = async () => {
      if (!nsCustomerId || !enabled || hasLookedUpCustomer.current) {
        return
      }

      hasLookedUpCustomer.current = true
      setIsLoading(true)

      try {
        const response = await ssapi.getNetSuiteCustomers({ nsCustomerId })
        // Check if component is still mounted before updating state
        if (!isMountedRef.current) return

        const netsuiteCustomer = response?.data?.[0]

        if (netsuiteCustomer) {
          const schoolData = transformNetSuiteCustomerForForm(netsuiteCustomer)
          onLookupComplete(schoolData)
        } else {
          // If no customer found, still call onLookupComplete to signal completion
          onLookupComplete({
            city: '',
            companyName: '',
            country: '',
            netsuiteCustomerId: null,
            nsCustomerId: nsCustomerId,
            organizationName: '',
            region: '',
          })
        }
      } catch (err) {
        console.warn('Failed to lookup NetSuite customer:', err)
        // Check if component is still mounted before updating state
        if (!isMountedRef.current) return

        // Even on error, call onLookupComplete to signal completion
        onLookupComplete({
          city: '',
          companyName: '',
          country: '',
          netsuiteCustomerId: null,
          nsCustomerId: nsCustomerId,
          organizationName: '',
          region: '',
        })
      } finally {
        // Only update loading state if component is still mounted
        if (isMountedRef.current) setIsLoading(false)
      }
    }

    lookupNetSuiteCustomer()

    // Cleanup function to mark component as unmounted
    return () => {
      isMountedRef.current = false
    }
  }, [nsCustomerId, enabled, onLookupComplete])

  return {
    isLoading,
  }
}
